import React from 'react';
import {
  ArrowRight,
  Bot,
  ClipboardCheck,
  Code2,
  FileSearch,
  FileText,
  MessageCircle,
  Mic,
  ShieldCheck,
  UserCheck,
  Users,
  Video,
} from 'lucide-react';
import { Button } from '@/Components/ui/button';
import Header from '../Header&Footer/Header';
import Footer from '../Header&Footer/Footer';
import useSEO from '../HomePage/useSEO';
import ScrollReveal from '../HomePage/ScrollReveal';
import {
  DEMO_PATH,
  IndustryCta,
  IndustryFlowStyles,
  IndustryWorkflow,
  Section,
  SectionIntro,
} from '../Industries/industryUi';

const PLATFORM_MODULES = [
  {
    title: 'AI Job Creation',
    detail: 'Create a job description from text, voice, or an uploaded document.',
    icon: FileText,
  },
  {
    title: 'AI Resume Intelligence',
    detail: 'Parse, score, and rank resumes against the job description.',
    icon: FileSearch,
    href: '/hirewithus/ai-sourcing-screening',
  },
  {
    title: 'AI Video Interview',
    detail: 'Run structured AI video interviews for candidate screening.',
    icon: Video,
    href: '/hirewithus/virtualhiring',
  },
  {
    title: 'AI Voice Screening',
    detail: 'Ask role-specific screening questions by AI voice call.',
    icon: Mic,
    href: '/hirewithus/ai-call-screening',
  },
  {
    title: 'WhatsApp Recruiting',
    detail: 'Keep screening and candidate updates in one WhatsApp workflow.',
    icon: MessageCircle,
  },
  {
    title: 'Technical Assessment',
    detail: 'Run proctored coding tests for technical roles.',
    icon: Code2,
    href: '/hirewithus/coding-assessment',
  },
  {
    title: 'AI Follow-Up Interview',
    detail: 'Discuss a coding submission with AI follow-up questions.',
    icon: Bot,
    href: '/hirewithus/coding-assessment',
  },
  {
    title: 'Cheating Detection',
    detail: 'Review proctoring signals and evidence on coding assessments.',
    icon: ShieldCheck,
    href: '/hirewithus/coding-assessment',
  },
  {
    title: 'Behavioural Assessment',
    detail: 'Assess behavioural, personality, and communication signals.',
    icon: Users,
    href: '/hirewithus/functional-assessment',
  },
  {
    title: 'Live Interviews',
    detail: 'Run live virtual interviews with the hiring team.',
    icon: UserCheck,
    href: '/hirewithus/virtualhiring',
  },
];

const WORKFLOW = [
  { title: 'Create', detail: 'Set the role details and the criteria for the hiring process.', icon: FileText },
  { title: 'Source', detail: 'Build a relevant candidate pipeline for the role.', icon: FileSearch },
  { title: 'Screen', detail: 'Review resumes and early candidate responses.', icon: ClipboardCheck },
  { title: 'Assess', detail: 'Run role-aligned assessments where they are needed.', icon: Code2 },
  { title: 'Interview', detail: 'Use AI or live interviews that your team can review.', icon: Video },
  { title: 'Shortlist', detail: 'Compare candidate evidence before deciding who moves forward.', icon: UserCheck },
  { title: 'Hire', detail: 'Your hiring team makes the final decision.', icon: Users },
];

export default function Platform() {
  useSEO({
    title: 'AI Hiring Platform | SearchJob.ai',
    description:
      'End-to-end AI hiring platform for job creation, resume screening, assessments, interviews, candidate intelligence and hiring.',
    canonical: 'https://searchjob.ai/platform',
    ogImage: 'https://searchjob.ai/og-image.png',
  });

  return (
    <div className="min-h-screen overflow-x-hidden bg-background">
      <Header />
      <main className="pt-16">
        <Hero />
        <ScrollReveal>
          <PlatformModules />
        </ScrollReveal>
        <ScrollReveal>
          <ConnectedWorkflow />
        </ScrollReveal>
        <FinalCta />
      </main>
      <Footer />
      <IndustryFlowStyles />
    </div>
  );
}

function Hero() {
  return (
    <section className="relative isolate overflow-hidden bg-background">
      <div className="pointer-events-none absolute inset-0 -z-10">
        <div className="absolute right-0 top-0 h-64 w-64 rounded-full bg-primary/5 blur-3xl" />
      </div>
      <div className="mx-auto grid max-w-7xl items-start gap-12 px-4 pt-12 pb-16 sm:px-6 lg:grid-cols-2 lg:gap-14 lg:px-8 lg:pt-16">
        <div className="text-center lg:text-left">
          <p className="mb-5 inline-flex items-center gap-2 rounded-full border border-primary/20 bg-primary/5 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-primary">
            <Bot className="h-3.5 w-3.5" aria-hidden="true" />
            AI hiring platform
          </p>
          <h1 className="mb-6 text-4xl font-bold leading-[1.06] tracking-[-0.02em] text-foreground sm:text-5xl lg:text-[3.4rem] xl:text-6xl">
            The AI Hiring Platform <span className="bg-gradient-to-r from-primary via-emerald-600 to-teal-600 bg-clip-text text-transparent">for Modern Talent Teams.</span>
          </h1>
          <p className="mx-auto mb-8 max-w-2xl text-lg leading-relaxed text-muted-foreground sm:text-xl lg:mx-0">
            From Job Description to Offer Letter—one intelligent hiring workflow.
          </p>
          <div className="flex flex-col justify-center gap-3 sm:flex-row lg:justify-start">
            <Button
              asChild
              size="lg"
              className="!h-12 !rounded-full !bg-primary px-8 text-base font-semibold !text-primary-foreground shadow-lg shadow-primary/25 group hover:!bg-primary/90"
            >
              <a href={DEMO_PATH}>
                Book a Platform Demo
                <ArrowRight className="ml-1.5 h-4 w-4 transition-transform group-hover:translate-x-0.5" aria-hidden="true" />
              </a>
            </Button>
            <Button
              asChild
              size="lg"
              variant="outline"
              className="!h-12 !rounded-full bg-card/70 px-8 text-base font-semibold backdrop-blur-md"
            >
              <a href="#platform-modules">Explore the Platform</a>
            </Button>
          </div>
        </div>
        <PlatformOverview />
      </div>
    </section>
  );
}

function PlatformOverview() {
  const stages = [
    ['Role setup', 'Job creation and resume intelligence'],
    ['Candidate evidence', 'Screening, assessments, and interviews'],
    ['Team decision', 'Your team reviews and decides'],
  ];

  return (
    <aside className="relative self-start" aria-label="Connected hiring workflow">
      <div className="pointer-events-none absolute -inset-4 -z-10 rounded-[3rem] bg-gradient-to-tr from-primary/20 via-emerald-500/10 to-teal-500/20 blur-3xl" />
      <p className="mb-4 text-xs font-semibold uppercase tracking-wider text-muted-foreground">One connected workflow</p>
      <ol className="space-y-3">
        {stages.map(([label, note], index) => (
          <li key={label}>
            <div className="flex items-start gap-3 rounded-2xl border border-primary bg-primary/5 px-4 py-3">
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border border-primary/30 bg-primary/10 text-sm font-semibold text-primary">
                {index + 1}
              </span>
              <span>
                <span className="block text-sm font-semibold text-foreground">{label}</span>
                <span className="mt-0.5 block text-sm leading-relaxed text-muted-foreground">{note}</span>
              </span>
            </div>
            {index < stages.length - 1 ? <span className="mx-auto my-1 block h-4 w-px bg-primary/40" aria-hidden="true" /> : null}
          </li>
        ))}
      </ol>
    </aside>
  );
}

function PlatformModules() {
  return (
    <Section id="platform-modules" muted>
      <SectionIntro
        eyebrow="Platform modules"
        title="Bring the hiring work into one connected platform."
        copy="Use the modules that fit the role, and keep the evidence available for your team to review."
      />
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {PLATFORM_MODULES.map((module) => (
          <PlatformModule key={module.title} module={module} />
        ))}
      </div>
    </Section>
  );
}

function PlatformModule({ module }) {
  const content = (
    <>
      <span className="mb-4 flex h-11 w-11 items-center justify-center rounded-xl border border-primary/30 bg-primary/10 text-primary transition-colors duration-200 group-hover:border-primary">
        <module.icon className="h-5 w-5" aria-hidden="true" />
      </span>
      <h3 className="text-base font-semibold text-foreground group-hover:text-primary">{module.title}</h3>
      <span className="mt-2 text-sm leading-relaxed text-muted-foreground">{module.detail}</span>
    </>
  );
  const className =
    'group flex min-h-11 flex-col rounded-2xl border border-primary bg-primary/5 p-5 transition duration-200 hover:-translate-y-0.5 hover:border-primary hover:shadow-md motion-reduce:transition-none motion-reduce:hover:translate-y-0 motion-reduce:hover:shadow-none';

  return module.href ? (
    <a
      href={module.href}
      className={`${className} focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary`}
    >
      {content}
    </a>
  ) : (
    <article className={className}>{content}</article>
  );
}

function ConnectedWorkflow() {
  return (
    <Section id="platform-workflow">
      <SectionIntro
        eyebrow="One hiring workflow"
        title="Move from role creation to a human hiring decision."
        copy="The platform keeps each step connected while your team stays in control of the final decision."
      />
      <IndustryWorkflow steps={WORKFLOW} />
    </Section>
  );
}

function FinalCta() {
  return (
    <IndustryCta
      title="See the platform in your hiring workflow."
      copy="Bring job creation, screening, assessments, and interviews into one connected hiring path."
      primaryLabel="Book a Platform Demo"
      primaryHref={DEMO_PATH}
    />
  );
}
